import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Check, Sparkles, X, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/site";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const serviceLabel = (slug) => SERVICES.find((s) => s.slug === slug)?.title || slug;

const StatusBadge = ({ status }) => {
  const styles = {
    new: "text-ember border-ember/40 bg-ember/10",
    read: "text-gray-300 border-white/20 bg-white/5",
    closed: "text-green-400 border-green-400/30 bg-green-400/10",
  };
  return (
    <span className={`label-industrial text-[10px] px-2 py-1 border ${styles[status] || styles.read}`} data-testid={`status-badge`}>
      {status}
    </span>
  );
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);
  const [assist, setAssist] = useState(null); // { enquiry, mode, text, loading }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setEnquiries([]);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/enquiries/${id}/status`, { status: "read" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const runAssist = async (enquiry, mode) => {
    setAssist({ enquiry, mode, text: "", loading: true });
    try {
      const token = localStorage.getItem("jcd_token");
      const res = await fetch(`${BACKEND}/api/admin/enquiry-assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ enquiry_id: enquiry.id, mode }),
      });
      if (!res.ok || !res.body) throw new Error("Assist failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(data);
            if (delta) setAssist((a) => (a ? { ...a, text: a.text + delta } : a));
          } catch (_) {}
        }
      }
    } catch (e) {
      toast.error("Could not generate. Please try again.");
      setAssist((a) => (a ? { ...a, text: a.text || "" } : a));
    } finally {
      setAssist((a) => (a ? { ...a, loading: false } : a));
    }
  };

  const copyAssist = () => {
    if (assist?.text) {
      navigator.clipboard.writeText(assist.text);
      toast.success("Copied to clipboard");
    }
  };

  if (loading || !user) return <div className="min-h-screen bg-[#0a0a0a]" />;

  const newCount = (enquiries || []).filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#111]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-logo">
            <img src="/logo.png" alt="JCD Customs & Marine logo" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-xs text-gray-500 hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout-btn"
              className="label-industrial text-gray-300 hover:text-ember transition-colors duration-150 flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="label-industrial text-ember mb-2">Admin</p>
            <h1 className="font-display text-5xl tracking-wide text-white" data-testid="admin-dashboard-heading">ENQUIRIES</h1>
          </div>
          <div className="flex gap-4">
            <div className="border border-white/10 bg-[#111] px-6 py-4" data-testid="stat-total">
              <p className="label-industrial text-gray-500 mb-1">Total</p>
              <p className="font-display text-3xl text-white">{enquiries?.length ?? 0}</p>
            </div>
            <div className="border border-ember/40 bg-ember/10 px-6 py-4" data-testid="stat-new">
              <p className="label-industrial text-ember mb-1">New</p>
              <p className="font-display text-3xl text-white">{newCount}</p>
            </div>
          </div>
        </div>

        {enquiries === null ? (
          <p className="text-gray-500 text-sm" data-testid="enquiries-loading">Loading enquiries...</p>
        ) : enquiries.length === 0 ? (
          <div className="border border-white/10 bg-[#111] p-16 text-center" data-testid="enquiries-empty">
            <p className="font-display text-3xl tracking-wide text-gray-500">NO ENQUIRIES YET</p>
            <p className="text-gray-600 text-sm mt-2">New bookings from the website will show up here.</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto" data-testid="enquiries-table-wrap">
            <table className="w-full text-sm" data-testid="enquiries-table">
              <thead>
                <tr className="border-b border-white/10 bg-[#111] text-left">
                  {["Received", "Name", "Contact", "Service", "Job Details", "Status", ""].map((h) => (
                    <th key={h} className="label-industrial text-gray-500 px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.map((enq) => (
                  <tr key={enq.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-100" data-testid={`enquiry-row-${enq.id}`}>
                    <td className="px-5 py-4 text-gray-500 whitespace-nowrap">
                      {new Date(enq.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4 text-white font-medium whitespace-nowrap">
                      {enq.name}
                      {enq.vehicle && <span className="block text-xs text-gray-500 font-normal">{enq.vehicle}</span>}
                    </td>
                    <td className="px-5 py-4 text-gray-400">
                      {enq.email}
                      <span className="block text-xs text-gray-600">{enq.phone}</span>
                    </td>
                    <td className="px-5 py-4 text-gray-300 whitespace-nowrap">{serviceLabel(enq.service)}</td>
                    <td className="px-5 py-4 text-gray-400 max-w-xs">
                      <p className="line-clamp-2">{enq.message}</p>
                      {enq.preferred_date && <span className="text-xs text-gray-600">Preferred: {enq.preferred_date}</span>}
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={enq.status} /></td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => runAssist(enq, "reply")} title="AI draft reply"
                          data-testid={`enquiry-assist-${enq.id}`}
                          className="p-2 border border-white/10 text-gray-400 hover:text-ember hover:border-ember/40 transition-colors duration-150">
                          <Sparkles className="w-4 h-4" />
                        </button>
                        {enq.status === "new" && (
                          <button onClick={() => markRead(enq.id)} title="Mark as read"
                            data-testid={`enquiry-read-${enq.id}`}
                            className="p-2 border border-white/10 text-gray-400 hover:text-ember hover:border-ember/40 transition-colors duration-150">
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => remove(enq.id)} title="Delete"
                          data-testid={`enquiry-delete-${enq.id}`}
                          className="p-2 border border-white/10 text-gray-400 hover:text-red-400 hover:border-red-400/40 transition-colors duration-150">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {assist && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="assist-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setAssist(null); }}>
          <div className="w-full max-w-lg border border-white/10 bg-[#111] shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-ember" />
                <p className="font-display text-xl tracking-wide text-white">AI ASSIST — {assist.enquiry.name}</p>
              </div>
              <button onClick={() => setAssist(null)} data-testid="assist-close-btn"
                className="text-gray-500 hover:text-white transition-colors duration-150">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-3 flex gap-2 border-b border-white/10">
              {[
                { key: "reply", label: "Draft Reply" },
                { key: "summary", label: "Summarise" },
              ].map((t) => (
                <button key={t.key} onClick={() => runAssist(assist.enquiry, t.key)} disabled={assist.loading}
                  data-testid={`assist-mode-${t.key}`}
                  className={`label-industrial text-xs px-3 py-2 border transition-colors duration-150 disabled:opacity-40 ${
                    assist.mode === t.key ? "text-ember border-ember/50 bg-ember/10" : "text-gray-400 border-white/10 hover:border-white/30"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="px-5 py-4">
              <div data-testid="assist-output"
                className="min-h-[8rem] max-h-[40vh] overflow-y-auto whitespace-pre-wrap text-sm text-gray-200 leading-relaxed bg-[#0a0a0a] border border-white/10 p-4">
                {assist.text || (assist.loading ? "Thinking…" : "")}
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={copyAssist} disabled={!assist.text || assist.loading}
                  data-testid="assist-copy-btn"
                  className="label-industrial text-xs flex items-center gap-2 px-4 py-2 bg-ember text-black hover:bg-ember-hover transition-colors duration-150 disabled:opacity-40">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
